import { BulkSaveResult } from "pages/Accounts/accountContext";
import { LoadedFile } from "./types";
import { UpdateableColumn } from "./csvColumns";

export type Action =
  | { type: "SHOW_ACCOUNTS"; file: LoadedFile }
  | { type: "SHOW_FORMAT_ERRORS"; file: LoadedFile }
  | { type: "SHOW_SAVED_RESULTS" }
  | { type: "SHOW_IMPORT" }
  | { type: "SHOW_EXAMPLE" }
  | { type: "SHOW_SELECT_COLUMNS" }
  | { type: "ADD_FILES"; files: LoadedFile[] }
  | { type: "REMOVE_FILE"; index: number }
  | { type: "SAVING_ACCOUNTS"; emailsToDelete?: string[] }
  | { type: "SHOW_SAVING_ACCOUNTS" }
  | { type: "SAVE_ACCOUNTS_FINISHED"; results: BulkSaveResult }
  | { type: "SELECTED_COLUMNS"; attributes: UpdateableColumn[] }
  | { type: "BACK_FROM_EXAMPLE" };

export interface State {
  currentView:
    | "SELECT_COLUMNS"
    | "EXAMPLE"
    | "IMPORT"
    | "ACCOUNTS"
    | "SAVED_RESULTS"
    | "SAVING_ACCOUNTS"
    | "FORMAT_ERRORS";
  selectedFile: LoadedFile;
  selectedColumns: UpdateableColumn[];
  saveResults: BulkSaveResult;
  loadedFiles: LoadedFile[];
  emailsToDelete: string[];
  action: ActionType;
}

export type ActionType = "UPSERT" | "UPDATE" | "DELETE";

export function init(type: ActionType): State {
  return {
    action: type,
    currentView: type === "UPDATE" ? "SELECT_COLUMNS" : "IMPORT",
    selectedFile: null,
    saveResults: null,
    loadedFiles: [],
    emailsToDelete: [],
    selectedColumns: [],
  };
}

export default function reducer(state: State, action: Action): State {
  if (action.type === "BACK_FROM_EXAMPLE") {
    let currentView: State["currentView"] = "IMPORT";

    if (state.action === "UPDATE") {
      currentView = "SELECT_COLUMNS";
      if (state.selectedColumns.length > 0) {
        currentView = "IMPORT";
      }
    }

    return {
      ...state,
      currentView,
    };
  }

  if (action.type === "ADD_FILES") {
    const previous = state.loadedFiles;
    const update = addOrUpdate(action.files, previous);
    return {
      ...state,
      loadedFiles: update,
    };
  }

  if (action.type === "SELECTED_COLUMNS") {
    return {
      ...state,
      selectedColumns: action.attributes,
      currentView: "IMPORT",
    };
  }

  if (action.type === "SAVE_ACCOUNTS_FINISHED") {
    return {
      ...state,
      saveResults: action.results,
    };
  }

  if (action.type === "REMOVE_FILE") {
    const update = state.loadedFiles;
    update.splice(action.index, 1);
    return {
      ...state,
      loadedFiles: update,
    };
  }

  if (action.type === "SAVING_ACCOUNTS") {
    return {
      ...state,
      emailsToDelete: action.emailsToDelete || [],
      currentView: "SAVING_ACCOUNTS",
    };
  }

  if (action.type === "SHOW_SELECT_COLUMNS") {
    return {
      ...state,
      currentView: "SELECT_COLUMNS",
    };
  }

  if (action.type === "SHOW_SAVING_ACCOUNTS") {
    return {
      ...state,
      currentView: "SAVING_ACCOUNTS",
    };
  }

  if (action.type === "SHOW_ACCOUNTS") {
    return {
      ...state,
      currentView: "ACCOUNTS",
      selectedFile: action.file,
    };
  }

  if (action.type === "SHOW_FORMAT_ERRORS") {
    return {
      ...state,
      currentView: "FORMAT_ERRORS",
      selectedFile: action.file,
    };
  }

  if (action.type === "SHOW_SAVED_RESULTS") {
    return {
      ...state,
      currentView: "SAVED_RESULTS",
    };
  }

  if (action.type === "SHOW_IMPORT") {
    return {
      ...state,
      currentView: "IMPORT",
      selectedFile: null,
      saveResults: null,
    };
  }

  if (action.type === "SHOW_EXAMPLE") {
    return {
      ...state,
      currentView: "EXAMPLE",
      selectedFile: null,
      saveResults: null,
    };
  }

  return state;
}

function addOrUpdate(loaded: LoadedFile[], previous: LoadedFile[]) {
  const update = [...previous];

  loaded.forEach(f => {
    // Update if already exists
    const existingIndex = previous.findIndex(uf => uf.fileName === f.fileName);
    if (existingIndex > -1) {
      update[existingIndex] = f;
    } else {
      // Add if not
      update.push(f);
    }
  });

  return update;
}
